import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import { getShifts } from '../_services/classroom-shift.service';

export interface EditableCYPFields {
  name?: string | null;
  courseDisplayName?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  examStartDate?: string | null;
  examEndDate?: string | null;
  reexamDate?: string | null;
  credits?: number | null;
  localFee?: number | null;
  internationalFee?: number | null;
  adjustableAmount?: number | null;
  openForAddOrDrop?: number | null;
  shiftId?: number | null;
}

interface EditCYPDialogProps {
  open: boolean;
  record: {
    id: number;
    moduleCode?: string;
    moduleName?: string;
  } & EditableCYPFields;
  onSave: (id: number, fields: Partial<EditableCYPFields>) => Promise<void>;
  onClose: () => void;
}

const toDateInput = (val: string | null | undefined): string => {
  if (!val) return '';
  // Handle ISO datetime strings
  return val.substring(0, 10);
};

const EditCYPDialog: React.FC<EditCYPDialogProps> = ({ open, record, onSave, onClose }) => {
  const [form, setForm] = useState<EditableCYPFields>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [shifts, setShifts] = useState<Array<{ id: number; name: string }>>([]);

  useEffect(() => {
    if (open && record) {
      setForm({
        name: record.name || '',
        courseDisplayName: record.courseDisplayName || '',
        startDate: toDateInput(record.startDate),
        endDate: toDateInput(record.endDate),
        examStartDate: toDateInput(record.examStartDate),
        examEndDate: toDateInput(record.examEndDate),
        reexamDate: toDateInput(record.reexamDate),
        credits: record.credits ?? null,
        localFee: record.localFee ?? null,
        internationalFee: record.internationalFee ?? null,
        adjustableAmount: record.adjustableAmount ?? null,
        openForAddOrDrop: record.openForAddOrDrop ?? 0,
        shiftId: record.shiftId ?? null,
      });
      setError('');
    }
  }, [open, record]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const response: any = await getShifts({ limit: 500 });
        if (cancelled) return;
        const raw =
          (Array.isArray(response?.data) && response.data) ||
          response?.shifts ||
          response?.data?.shifts ||
          [];
        const list = raw.map((s: any) => ({
          id: s.id,
          name: s.name,
        }));
        setShifts(list);
      } catch {
        if (!cancelled) setShifts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleChange = (field: keyof EditableCYPFields, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleNumberChange = (field: keyof EditableCYPFields, raw: string) => {
    if (raw === '') {
      handleChange(field, null);
    } else {
      const num = parseFloat(raw);
      if (!isNaN(num)) handleChange(field, num);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      // Only send fields that changed
      const changed: Partial<EditableCYPFields> = {};
      const original: any = record;
      for (const key of Object.keys(form) as (keyof EditableCYPFields)[]) {
        const formVal = form[key];
        let origVal = original[key];
        // Normalize dates for comparison
        if (['startDate', 'endDate', 'examStartDate', 'examEndDate', 'reexamDate'].includes(key)) {
          origVal = toDateInput(origVal);
        }
        // Normalize nulls
        const a = formVal === '' ? null : formVal;
        const b = origVal === '' || origVal === undefined ? null : origVal;
        if (a !== b) {
          (changed as any)[key] = a;
        }
      }

      if (Object.keys(changed).length === 0) {
        onClose();
        return;
      }

      await onSave(record.id, changed);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box>
          <Typography variant="h6" style={{ fontWeight: 600 }}>Edit Module Properties</Typography>
          {record?.moduleCode && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
              {record.moduleCode}{record.moduleName ? ` - ${record.moduleName}` : ''}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* General */}
          <Typography variant="subtitle2" style={{ color: 'var(--text-body)', marginBottom: -8 }}>General</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name"
                value={form.name || ''}
                onChange={e => handleChange('name', e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Course Display Name"
                value={form.courseDisplayName || ''}
                onChange={e => handleChange('courseDisplayName', e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
              />
            </Grid>
          </Grid>

          <Divider />

          {/* Dates */}
          <Typography variant="subtitle2" style={{ color: 'var(--text-body)', marginBottom: -8 }}>Module Dates</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Date"
                type="date"
                value={form.startDate || ''}
                onChange={e => handleChange('startDate', e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="End Date"
                type="date"
                value={form.endDate || ''}
                onChange={e => handleChange('endDate', e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" style={{ color: 'var(--text-body)', marginBottom: -8 }}>Exam Dates</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Exam Start"
                type="date"
                value={form.examStartDate || ''}
                onChange={e => handleChange('examStartDate', e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Exam End"
                type="date"
                value={form.examEndDate || ''}
                onChange={e => handleChange('examEndDate', e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Reexam Date"
                type="date"
                value={form.reexamDate || ''}
                onChange={e => handleChange('reexamDate', e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Divider />

          {/* Academics */}
          <Typography variant="subtitle2" style={{ color: 'var(--text-body)', marginBottom: -8 }}>Academics</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                label="Credits"
                type="number"
                value={form.credits ?? ''}
                onChange={e => handleNumberChange('credits', e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                inputProps={{ step: 0.5, min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.openForAddOrDrop === 1}
                    onChange={e => handleChange('openForAddOrDrop', e.target.checked ? 1 : 0)}
                    color="primary"
                  />
                }
                label="Open for Add/Drop"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel id="cyp-shift-label">Default Shift (optional)</InputLabel>
                <Select
                  labelId="cyp-shift-label"
                  value={form.shiftId ?? ''}
                  onChange={e => handleChange('shiftId', e.target.value === '' ? null : Number(e.target.value))}
                  label="Default Shift (optional)"
                >
                  <MenuItem value="">None</MenuItem>
                  {shifts.map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider />

          {/* Fees */}
          <Typography variant="subtitle2" style={{ color: 'var(--text-body)', marginBottom: -8 }}>Fees</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Local Fee"
                type="number"
                value={form.localFee ?? ''}
                onChange={e => handleNumberChange('localFee', e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                inputProps={{ step: 0.01, min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="International Fee"
                type="number"
                value={form.internationalFee ?? ''}
                onChange={e => handleNumberChange('internationalFee', e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                inputProps={{ step: 0.01, min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Adjustable Amount"
                type="number"
                value={form.adjustableAmount ?? ''}
                onChange={e => handleNumberChange('adjustableAmount', e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                inputProps={{ step: 0.01, min: 0 }}
              />
            </Grid>
          </Grid>

          {error && (
            <Typography variant="body2" style={{ color: '#d32f2f', marginTop: 4 }}>
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px' }}>
        <Button onClick={onClose} variant="outlined">Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving}
          style={{ minWidth: 120 }}
        >
          {saving ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCYPDialog;
